import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryV1 } from './baseQueryV1';
import { setUser } from './slice/userSlice';
import { employeeLeaveApi } from './employeeLeaveApi';

// Logs / log requests change the unplanned leaves (a day moves out of one), so refresh them too
const refreshUnplannedLeaves = async (_arg, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled;
    dispatch(employeeLeaveApi.util.invalidateTags(['EmployeeLeave']));
  } catch {
    // the request failed, nothing changed
  }
};

export const timeLogApi = createApi({
  reducerPath: 'timeLogApi',
  baseQuery: baseQueryV1,
  tagTypes: ['timeLogs', 'timeLogsByDate', 'monthlyTimeLogs'],
  endpoints: (builder) => ({
    getTimeLogs: builder.query({
      query: (params) => ({
        url: 'time-logs',
        params,
      }),
      transformResponse: (res) => res?.data,
      providesTags: ['timeLogs'],
    }),
    getAllTimeLogsByDate: builder.query({
      query: ({ date }) => ({
        url: 'time-logs/by-date',
        params: { date },
      }),
      transformResponse: (res) => res?.data || { timeLogs: [], leaves: [], holiday: null },
      providesTags: ['timeLogs'],
    }),
    checkIn: builder.mutation({
      query: ({ id, payload }) => ({
        url: `time-logs/check-in/${id}`,
        method: 'POST',
        body: payload,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const currentUser = getState().user.user;
          dispatch(setUser({ ...currentUser, session_status: 'ACTIVE', lastCheckOutTime: null }));
        } catch (error) {
          console.error('Fetching user details failed:', error);
        }
      },
      invalidatesTags: ['timeLogsByDate'],
    }),
    checkOut: builder.mutation({
      query: ({ id, payload }) => ({
        url: `time-logs/check-out/${id}`,
        method: 'POST',
        body: payload,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const currentUser = getState().user.user;
          dispatch(
            setUser({
              ...currentUser,
              session_status: 'INACTIVE',
              lastCheckOutTime: data?.data?.check_out || new Date(),
            }),
          );
        } catch (error) {
          console.error('Fetching user details failed:', error);
        }
      },
      invalidatesTags: ['timeLogsByDate'],
    }),
    updateTimeLog: builder.mutation({
      query: (data) => ({
        url: `time-logs/${data?.id}`,
        method: 'Put',
        body: { check_in: data?.check_in, check_out: data?.check_out },
      }),
      transformResponse: (res) => res?.data,
      invalidatesTags: ['monthlyTimeLogs'],
    }),
    getTimeLogsByDate: builder.query({
      query: ({ date, id }) => ({
        url: `time-logs/user/${id}/date?date=${date?.split('T')[0]}`,
        method: 'Get',
      }),
      transformResponse: (res) => res?.data,
      providesTags: ['timeLogsByDate'],
    }),
    getMonthlyTimeLogsByUserId: builder.query({
      query: ({ month, year, id }) => ({
        url: `time-logs/user/${id}/month?month=${month}&year=${year}`,
        method: 'Get',
      }),
      providesTags: ['timeLogsByDate', 'monthlyTimeLogs'],
    }),
    checkOutOnLogout: builder.mutation({
      query: ({ id, payload }) => ({
        url: `time-logs/check-out/${id}`,
        method: 'POST',
        body: payload,
      }),
    }),
    getLastTimeLogByDate: builder.query({
      query: ({ date }) => ({
        url: `time-logs/last-time-log/${date}`,
        method: 'Get',
      }),
      transformResponse: (res) => res?.data,
      providesTags: (result, error, { date }) => [{ type: 'timeLogsByDate', id: date }],
    }),
    officialCheckout: builder.mutation({
      query: (payload) => ({
        url: `time-logs/official-checkout`,
        method: 'Post',
        body: payload,
      }),
      invalidatesTags: ['timeLogsByDate', 'monthlyTimeLogs'],
    }),
    checkOfficialCheckoutOverlaps: builder.mutation({
      query: (payload) => ({
        url: `time-logs/official-checkout/check-overlaps`,
        method: 'Post',
        body: payload,
      }),
      transformResponse: (res) => res?.data || [],
    }),

    addTimeLog: builder.mutation({
      query: ({ userId, ...payload }) => ({
        url: userId ? `time-logs/user/${userId}` : `time-logs`,
        method: 'Post',
        body: payload,
      }),
      invalidatesTags: ['timeLogsByDate', 'monthlyTimeLogs'],
    }),

    getLogUpdateRequest: builder.query({
      query: (id) => ({
        url: `time-logs/log-update-request/${id}`,
        method: 'GET',
      }),
    }),

    getLogUpdateRequests: builder.query({
      query: ({ year, month }) => ({
        url: `time-logs/log-update-request/all?year=${year}&month=${month}`,
        method: 'GET',
      }),
      providesTags: ['logUpdateRequests'],
    }),
    approveLogUpdateStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `time-logs/log-update-request/${id}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['logUpdateRequests', 'monthlyTimeLogs'],
    }),
    cancelLogUpdateRequest: builder.mutation({
      query: (id) => ({
        url: `time-logs/log-update-request/${id}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: ['logUpdateRequests', 'monthlyTimeLogs'],
      onQueryStarted: refreshUnplannedLeaves,
    }),
    bulkCancelLogUpdateRequest: builder.mutation({
      query: (ids) => ({
        url: `time-logs/log-update-request/cancel/bulk`,
        method: 'PUT',
        body: ids,
      }),
      invalidatesTags: ['logUpdateRequests', 'monthlyTimeLogs'],
      onQueryStarted: refreshUnplannedLeaves,
    }),
    approveLogUpdateBulk: builder.mutation({
      query: (data) => ({
        url: `time-logs/log-update-request/approve/bulk`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['logUpdateRequests', 'monthlyTimeLogs'],
      onQueryStarted: refreshUnplannedLeaves,
    }),
    getLogUpdateRequestByUserId: builder.query({
      query: ({ id, month, year, status }) => ({
        url: `time-logs/log-update-request/user/${id}?month=${month}&year=${year}&status=${status}`,
        method: 'GET',
      }),

      providesTags: ['logUpdateRequests'],
    }),
    bulkProcessDayLogs: builder.mutation({
      query: ({ userId, ...payload }) => ({
        url: userId ? `time-logs/bulk-day/${userId}` : `time-logs/bulk-day`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['monthlyTimeLogs', 'timeLogsByDate', 'logUpdateRequests'],
      onQueryStarted: refreshUnplannedLeaves,
    }),
  }),
});

export const {
  useGetTimeLogsQuery,
  useGetAllTimeLogsByDateQuery,
  useUpdateTimeLogMutation,
  useCheckInMutation,
  useCheckOutOnLogoutMutation,
  useCheckOutMutation,
  useGetTimeLogsByDateQuery,
  useGetMonthlyTimeLogsByUserIdQuery,
  useGetLastTimeLogByDateQuery,
  useOfficialCheckoutMutation,
  useCheckOfficialCheckoutOverlapsMutation,
  useAddTimeLogMutation,
  useGetLogUpdateRequestQuery,
  useLazyGetLogUpdateRequestQuery,
  useGetLogUpdateRequestsQuery,
  useApproveLogUpdateStatusMutation,
  useCancelLogUpdateRequestMutation,
  useBulkCancelLogUpdateRequestMutation,
  useApproveLogUpdateBulkMutation,
  useGetLogUpdateRequestByUserIdQuery,
  useBulkProcessDayLogsMutation,
} = timeLogApi;
